import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Wrench, Shirt, Refrigerator, Flame, AppWindow, PawPrint, Ruler, Archive, DoorOpen,
  ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle, Lock, Shield, BadgeCheck,
  Calendar, User, Mail, Phone as PhoneIcon, MapPin, Home as HomeIcon, ArrowRight, AlertCircle,
  Gift, Flame as FlameIcon,
} from "lucide-react";
import {
  BEDROOM_OPTIONS, BATHROOM_OPTIONS, FREQUENCY_OPTIONS, ADDON_DEFINITIONS, computeQuote,
} from "@/lib/pricing";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { IS_QUICKBOOKS, QB_PAYMENT_URL, PROVIDER_LABEL } from "@/lib/payments";
import BookingCalendar from "@/components/BookingCalendar";
import WhatToExpect from "@/components/WhatToExpect";
import { toast } from "sonner";

const ADDON_ICON_MAP = { Shirt, Refrigerator, Flame, AppWindow, PawPrint, Ruler, Archive, DoorOpen };

const STEPS = [
  { id: 1, label: "Service" },
  { id: 2, label: "Home Details" },
  { id: 3, label: "Frequency & Add-ons" },
  { id: 4, label: "Your Info" },
  { id: 5, label: "Secure Your Slot" },
];

const INITIAL_FORM = {
  serviceType: "standard",
  bedrooms: 2,
  bathrooms: 1,
  frequency: "one_time",
  addOns: {
    laundry: false, fridge: false, oven: false, windows: false,
    pets: false, baseboard: false, cabinet: false, basement: false,
  },
  fullName: "", email: "", phone: "",
  address: "", city: "Evansville", state: "IN", zip: "",
  preferredDate: "", preferredTime: "morning",
  notes: "",
  withinServiceArea: false,
  authorizeCardOnFile: false,
  consentCommunications: false,
};

export default function QuotePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [promoSettings, setPromoSettings] = useState(null);

  // SEO
  React.useEffect(() => {
    document.title = "Instant Booking — Cleaning Quote | Top-Notch Pro Solutions Evansville";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", "Get an instant, transparent quote for residential house cleaning in Evansville, IN. Standard or deep clean, recurring discounts up to 15% off, secure Stripe deposit. 20-mile service radius.");
  }, []);

  useEffect(() => {
    api.get("/settings/promo").then((r) => setPromoSettings(r.data)).catch(() => {});
  }, []);

  const quote = useMemo(() => computeQuote({
    serviceType: form.serviceType,
    bedrooms: form.bedrooms,
    bathrooms: form.bathrooms,
    frequency: form.frequency,
    addOns: form.addOns,
  }), [form]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleAddOn = (id) => setForm((f) => ({ ...f, addOns: { ...f.addOns, [id]: !f.addOns[id] } }));

  const goNext = () => setStep((s) => Math.min(STEPS.length, s + 1));
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const canProceed = () => {
    if (step === 4) {
      return form.fullName && form.email && form.phone && form.address && form.zip && form.preferredDate && form.preferredTime;
    }
    if (step === 5) {
      return form.withinServiceArea && form.authorizeCardOnFile && form.consentCommunications;
    }
    return true;
  };

  const submit = async () => {
    if (!canProceed()) {
      toast.error("Please complete all required fields and confirmations.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_type: form.serviceType,
        bedrooms: parseInt(form.bedrooms, 10),
        bathrooms: parseInt(form.bathrooms, 10),
        frequency: form.frequency,
        add_ons: form.addOns,
        base_price: quote.base,
        discount_amount: quote.discountAmount,
        addons_total: quote.addonsTotal,
        service_estimate: quote.serviceEstimate,
        deposit: quote.deposit,
        remaining_balance: quote.remainingBalance,
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        zip_code: form.zip,
        preferred_date: form.preferredDate,
        preferred_time: form.preferredTime,
        notes: form.notes,
        authorize_card_on_file: form.authorizeCardOnFile,
        within_service_area: form.withinServiceArea,
        consent_communications: form.consentCommunications,
        promo_unlocked: form.frequency === "weekly" ? "5th_visit_free" : null,
      };
      // 1) Create booking
      const bookingRes = await api.post("/bookings", payload);
      const booking = bookingRes.data;
      sessionStorage.setItem("tnp_pending_booking", JSON.stringify(booking));

      if (IS_QUICKBOOKS) {
        // QuickBooks path: booking is held as deposit_pending. Surface the QB pay link on success.
        setSubmitted(booking);
        setSubmitting(false);
        return;
      }

      // Stripe path
      const checkoutRes = await api.post("/payments/checkout/deposit", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "This time slot is no longer available. Please pick another.", { duration: 6000 });
        update({ preferredTime: null });
        setStep(4); // back to the calendar step
      } else {
        toast.error(detail || "Could not submit your booking. Please try again.");
      }
      setSubmitting(false);
    }
  };

  if (submitted) {
    // Legacy path: should never trigger now that we redirect to Stripe before showing success.
    // Kept as a defensive fallback in case the redirect fails.
    return <BookingSuccess booking={submitted} quote={quote} onReset={() => { setSubmitted(null); setForm(INITIAL_FORM); setStep(1); }} />;
  }

  return (
    <div data-testid="quote-page" className="max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-16">
      <div className="max-w-3xl">
        <span className="section-eyebrow">Instant Booking</span>
        <h1 className="heading-display mt-3 text-4xl md:text-5xl">Build Your Custom Quote.</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Transparent matrix pricing for residential cleaning. See your real cost update in real time as you customize.
        </p>
      </div>

      {/* Stepper */}
      <div className="mt-10 mb-8">
        <div className="flex items-center justify-between max-w-3xl">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center text-center flex-1">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                    step >= s.id ? "bg-[var(--brand-teal)] text-white" : "bg-white border-2 border-[#dcd5c6] text-slate-500"
                  }`}
                  data-testid={`step-indicator-${s.id}`}
                >
                  {step > s.id ? <CheckCircle2 className="w-5 h-5" /> : s.id}
                </div>
                <span className={`mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wider ${step >= s.id ? "text-[var(--brand-teal)]" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${step > s.id ? "bg-[var(--brand-teal)]" : "bg-[#dcd5c6]"}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT: Stepper Content */}
        <div className="lg:col-span-2">
          <div className="card-elevated p-6 md:p-10">

            {step === 1 && (
              <StepService form={form} update={update} />
            )}
            {step === 2 && (
              <StepHomeDetails form={form} update={update} />
            )}
            {step === 3 && (
              <StepFreqAddons form={form} update={update} toggleAddOn={toggleAddOn} promoSettings={promoSettings} />
            )}
            {step === 4 && (
              <StepCustomerInfo form={form} update={update} />
            )}
            {step === 5 && (
              <StepCheckout form={form} update={update} quote={quote} onSubmit={submit} submitting={submitting} />
            )}

            <div className="mt-10 flex items-center justify-between border-t border-[#e6e0d4] pt-6">
              <button
                onClick={goPrev}
                disabled={step === 1}
                className={`btn-outline ${step === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="step-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              {step < STEPS.length && (
                <button
                  onClick={() => { if (canProceed()) goNext(); else toast.error("Please complete required fields."); }}
                  className="btn-primary"
                  data-testid="step-next-btn"
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Live Quote Summary + What to Expect */}
        <div className="lg:col-span-1 space-y-5">
          <QuoteSummary form={form} quote={quote} />
          <WhatToExpect variant="booking" />
        </div>
      </div>
    </div>
  );
}

// ------------------ STEPS ------------------

function StepService({ form, update }) {
  return (
    <div data-testid="step-1-service">
      <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>Choose Your Cleaning Type</h2>
      <p className="text-slate-600 mb-6 text-sm">Standard for routine refreshes, Deep Clean for a full top-to-bottom reset.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { id: "standard", title: "Standard Cleaning", desc: "Routine recurring care — kitchens, baths, dust, floors.", icon: Sparkles, color: "var(--brand-teal)" },
          { id: "deep", title: "Deep Cleaning", desc: "Everything in Standard + baseboards, inside cabinets, full appliance interiors.", icon: Wrench, color: "var(--brand-gold)" },
        ].map((o) => (
          <button
            key={o.id}
            onClick={() => update({ serviceType: o.id })}
            data-testid={`service-tab-${o.id}`}
            className={`text-left p-6 rounded-2xl border-2 transition-all ${
              form.serviceType === o.id
                ? "border-[var(--brand-teal)] bg-[var(--brand-teal)]/5"
                : "border-[#dcd5c6] bg-white hover:border-[var(--brand-gold)]"
            }`}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${o.color}22`, color: o.color }}>
              <o.icon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-lg text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>{o.title}</h3>
            <p className="text-sm text-slate-600 mt-1">{o.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepHomeDetails({ form, update }) {
  return (
    <div data-testid="step-2-home">
      <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>Your Home Size</h2>
      <p className="text-slate-600 mb-6 text-sm">We use a transparent matrix — your price is exactly what you see, no surprise fees.</p>

      <div className="space-y-8">
        <div>
          <label className="block text-sm font-semibold text-[var(--brand-teal)] mb-3 uppercase tracking-wider">Bedrooms</label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {BEDROOM_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => update({ bedrooms: n })}
                data-testid={`bedrooms-${n}`}
                className={`py-3 rounded-xl font-bold transition-all ${
                  form.bedrooms === n ? "bg-[var(--brand-teal)] text-white shadow-md" : "bg-white border-2 border-[#dcd5c6] text-slate-700 hover:border-[var(--brand-gold)]"
                }`}
              >
                {n}{n === 6 ? "+" : ""}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--brand-teal)] mb-3 uppercase tracking-wider">Bathrooms</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {BATHROOM_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => update({ bathrooms: n })}
                data-testid={`bathrooms-${n}`}
                className={`py-3 rounded-xl font-bold transition-all ${
                  form.bathrooms === n ? "bg-[var(--brand-teal)] text-white shadow-md" : "bg-white border-2 border-[#dcd5c6] text-slate-700 hover:border-[var(--brand-gold)]"
                }`}
              >
                {n}{n === 5 ? "+" : ""}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepFreqAddons({ form, update, toggleAddOn, promoSettings }) {
  const addons = ADDON_DEFINITIONS(form.serviceType).filter(a => a.tabs.includes(form.serviceType));
  const isWeekly = form.frequency === "weekly";
  const showScarcity = promoSettings?.scarcity_enabled && (promoSettings?.weekly_slots_left ?? 0) > 0;
  const scarcityText = promoSettings?.custom_message?.trim() || (showScarcity
    ? `Only ${promoSettings.weekly_slots_left} weekly slot${promoSettings.weekly_slots_left === 1 ? "" : "s"} left this week in your area`
    : null);
  return (
    <div data-testid="step-3-extras">
      <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>Frequency & Add-ons</h2>
      <p className="text-slate-600 mb-6 text-sm">Recurring clients save up to 15% off every visit.</p>

      {/* EARLY-BIRD PROMO BANNER */}
      <div
        data-testid="promo-banner"
        className={`relative overflow-hidden rounded-2xl p-5 mb-6 border-2 transition-all ${
          isWeekly
            ? "bg-gradient-to-r from-[var(--brand-gold)] to-[var(--brand-gold-deep)] border-[var(--brand-gold-deep)] text-white shadow-lg"
            : "bg-[var(--brand-cream-soft)] border-dashed border-[var(--brand-gold)]/70 text-[var(--brand-teal)]"
        }`}
      >
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            isWeekly ? "bg-white text-[var(--brand-gold-deep)]" : "bg-[var(--brand-gold)] text-white"
          }`}>
            <Gift className="w-6 h-6" />
          </div>
          <div className="flex-1">
            {isWeekly ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-white text-[var(--brand-gold-deep)] px-2 py-0.5 rounded-full" data-testid="promo-unlocked-badge">
                    🎉 Unlocked
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-extrabold text-white" style={{ fontFamily: "Outfit" }}>
                  Your 5th Weekly Visit is FREE!
                </h3>
                <p className="text-white/90 text-sm mt-1 leading-relaxed">
                  Stack with your 15% weekly discount — book 4 visits, your 5th one is on the house.
                  We'll auto-apply it on your 5th scheduled clean.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[var(--brand-gold)] text-white px-2 py-0.5 rounded-full">
                    Limited Offer
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-extrabold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>
                  Book Weekly — Get Your 5th Visit FREE
                </h3>
                <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                  Switch to weekly service and we'll waive your 5th visit's cost — stacks on top of your 15% weekly discount for serious savings.
                </p>
                <button
                  onClick={() => update({ frequency: "weekly" })}
                  data-testid="promo-claim-btn"
                  className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--brand-gold-deep)] hover:text-[var(--brand-teal)] transition"
                >
                  Claim This Offer <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* SCARCITY CUE */}
      {showScarcity && scarcityText && (
        <div
          data-testid="scarcity-cue"
          className="mb-6 -mt-3 mx-1 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
        >
          <FlameIcon className="w-4 h-4 text-red-600 flex-shrink-0 animate-pulse" />
          <span className="font-semibold">{scarcityText}</span>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-[var(--brand-teal)] mb-3 uppercase tracking-wider">Service Frequency</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {FREQUENCY_OPTIONS.map((f) => (
            <button
              key={f.id}
              onClick={() => update({ frequency: f.id })}
              data-testid={`frequency-${f.id}`}
              className={`relative p-4 rounded-xl text-left transition-all border-2 ${
                form.frequency === f.id ? "border-[var(--brand-teal)] bg-[var(--brand-teal)]/5" : "border-[#dcd5c6] bg-white hover:border-[var(--brand-gold)]"
              }`}
            >
              {f.id === "weekly" && (
                <span className="absolute -top-2 -right-2 text-[9px] font-bold uppercase tracking-wider bg-[var(--brand-gold)] text-white px-2 py-0.5 rounded-full shadow-md">
                  5th Free
                </span>
              )}
              <div className="font-bold text-[var(--brand-teal)] text-sm" style={{ fontFamily: "Outfit" }}>{f.label}</div>
              <div className={`text-xs mt-1 ${f.discount > 0 ? "text-[var(--brand-gold-deep)] font-semibold" : "text-slate-500"}`}>{f.sub}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <label className="block text-sm font-semibold text-[var(--brand-teal)] mb-3 uppercase tracking-wider">Add-On Services</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {addons.map((a) => {
            const Icon = ADDON_ICON_MAP[a.icon] || Sparkles;
            const active = form.addOns[a.id];
            return (
              <label
                key={a.id}
                data-testid={`addon-${a.id}`}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  active ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/8" : "border-[#dcd5c6] bg-white hover:border-[var(--brand-gold)]/60"
                }`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleAddOn(a.id)}
                  className="w-5 h-5 accent-[var(--brand-teal)] cursor-pointer"
                />
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-[var(--brand-gold)] text-white" : "bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[var(--brand-teal)] text-sm" style={{ fontFamily: "Outfit" }}>{a.label}</div>
                  <div className="text-xs text-slate-500">+${a.price.toFixed(2)}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepCustomerInfo({ form, update }) {
  return (
    <div data-testid="step-4-info">
      <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>Your Information</h2>
      <p className="text-slate-600 mb-6 text-sm">All fields are protected with 256-bit SSL encryption.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field icon={User} label="Full Name *" testid="input-full-name">
          <input type="text" value={form.fullName} onChange={(e) => update({ fullName: e.target.value })} className="form-input" placeholder="Jane Doe" data-testid="input-full-name" />
        </Field>
        <Field icon={Mail} label="Email *" testid="input-email">
          <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} className="form-input" placeholder="jane@example.com" data-testid="input-email" />
        </Field>
        <Field icon={PhoneIcon} label="Phone *" testid="input-phone">
          <input type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} className="form-input" placeholder="(812) 555-0100" data-testid="input-phone" />
        </Field>
        <Field icon={MapPin} label="Street Address *" testid="input-address">
          <input type="text" value={form.address} onChange={(e) => update({ address: e.target.value })} className="form-input" placeholder="123 Main St" data-testid="input-address" />
        </Field>
        <Field icon={HomeIcon} label="City *">
          <input type="text" value={form.city} onChange={(e) => update({ city: e.target.value })} className="form-input" data-testid="input-city" />
        </Field>
        <Field icon={MapPin} label="ZIP Code *">
          <input type="text" value={form.zip} onChange={(e) => update({ zip: e.target.value })} className="form-input" placeholder="47715" data-testid="input-zip" />
        </Field>
        <div className="md:col-span-2 mt-2">
          <label className="block text-xs font-semibold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Pick Your Date & Time *</label>
          <BookingCalendar
            selectedDate={form.preferredDate}
            selectedTime={form.preferredTime}
            onChange={({ date, time }) => update({ preferredDate: date, preferredTime: time })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Anything we should know? (optional)</label>
          <textarea value={form.notes} onChange={(e) => update({ notes: e.target.value })} className="form-input min-h-[100px] resize-y" placeholder="Gate code, pet info, special instructions..." data-testid="input-notes" />
        </div>
      </div>

      <style>{`.form-input { width:100%; padding:12px 14px; border:1.5px solid #dcd5c6; border-radius:10px; background:white; font-family:inherit; color:#0f172a; font-size:14px; transition:border-color 0.2s; }
      .form-input:focus { outline:none; border-color:var(--brand-teal); box-shadow:0 0 0 3px rgba(13,91,94,0.1); }`}</style>
    </div>
  );
}

function StepCheckout({ form, update, quote, onSubmit, submitting }) {
  return (
    <div data-testid="step-5-checkout">
      <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>Secure Your Slot</h2>
      <p className="text-slate-600 mb-6 text-sm">A flat $50 deposit locks in your appointment. The remaining balance is processed only after your service is completed to your satisfaction.</p>

      {/* SERVICE AREA NOTICE */}
      <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-5 mb-5" data-testid="service-area-notice">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-900 text-sm">Instant Booking — Cleaning Within 20 Miles of Evansville</p>
            <p className="text-amber-800 text-xs mt-1 leading-relaxed">
              Our instant online booking is set up for cleaning services <strong>within 20 miles of Evansville, IN</strong>.
              Live further out or need a different service? We'd love to put together a personalized quote — <Link to="/request-quote" className="font-bold underline">request one here</Link> or call <a href={BRAND.phoneHref} className="font-bold underline">{BRAND.phone}</a>.
            </p>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.withinServiceArea}
                onChange={(e) => update({ withinServiceArea: e.target.checked })}
                className="w-4 h-4 accent-amber-600"
                data-testid="confirm-service-area"
              />
              <span className="text-sm text-amber-900 font-semibold">I confirm my address is within 20 miles of Evansville, IN.</span>
            </label>
          </div>
        </div>
      </div>

      {/* PRICE SUMMARY (live) */}
      <div className="rounded-2xl border-2 border-[var(--brand-teal)] bg-[var(--brand-teal)]/5 p-6 mb-6" data-testid="checkout-summary">
        <h3 className="font-bold text-[var(--brand-teal)] mb-4" style={{ fontFamily: "Outfit" }}>Price Breakdown</h3>
        <ul className="space-y-2 text-sm">
          <li className="flex justify-between"><span className="text-slate-700">Total Service Estimate</span><span className="font-bold text-[var(--brand-teal)]" data-testid="checkout-total">${quote.serviceEstimate.toFixed(2)}</span></li>
          <li className="flex justify-between text-[var(--brand-gold-deep)]"><span>Due Today (Secure Booking Deposit)</span><span className="font-bold" data-testid="checkout-deposit">$50.00</span></li>
          <li className="flex justify-between border-t border-[var(--brand-teal)]/30 pt-2 mt-2"><span className="text-slate-700">Remaining Balance (after completion)</span><span className="font-bold" data-testid="checkout-remaining">${quote.remainingBalance.toFixed(2)}</span></li>
        </ul>
      </div>

      {/* CHECKOUT BLOCK */}
      <div className="rounded-2xl bg-gradient-to-br from-white to-[var(--brand-cream-soft)] border-2 border-[var(--brand-gold)] p-6 md:p-8" data-testid="stripe-checkout-block">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--brand-teal)] text-white text-xs font-bold uppercase tracking-wider mb-4">
            <Lock className="w-3.5 h-3.5" /> Secure {PROVIDER_LABEL} Checkout
          </div>
          <h3 className="text-2xl font-extrabold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>
            $50.00 Secure Booking Deposit
          </h3>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
            {IS_QUICKBOOKS ? (
              <>After you submit, you'll be taken to a confirmation screen with a one-click <strong>QuickBooks payment link</strong> for your $50 deposit. The remaining balance is invoiced after service.</>
            ) : (
              <>One-tap checkout with <strong>card, Apple Pay, or Google Pay</strong>. After your service is complete, we'll send you a secure payment link for the remaining balance.</>
            )}
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <span className="px-3 py-1.5 bg-white border border-[#dcd5c6] rounded-md">VISA</span>
            <span className="px-3 py-1.5 bg-white border border-[#dcd5c6] rounded-md">Mastercard</span>
            <span className="px-3 py-1.5 bg-white border border-[#dcd5c6] rounded-md">Amex</span>
            <span className="px-3 py-1.5 bg-white border border-[#dcd5c6] rounded-md">Discover</span>
          </div>
        </div>
      </div>

      {/* AUTHORIZATION ACKNOWLEDGEMENT */}
      <div className="mt-5 rounded-xl bg-[var(--brand-cream-soft)] border border-[#dcd5c6] p-5" data-testid="authorize-card-block">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.authorizeCardOnFile}
            onChange={(e) => update({ authorizeCardOnFile: e.target.checked })}
            className="w-5 h-5 accent-[var(--brand-teal)] mt-0.5 flex-shrink-0"
            data-testid="checkbox-authorize-card"
          />
          <span className="text-sm text-slate-800 leading-relaxed">
            I understand my <strong>$50 deposit</strong> is processed today via {PROVIDER_LABEL} to secure my appointment, and that the remaining balance of
            <span className="text-[var(--brand-teal)] font-bold"> ${quote.remainingBalance.toFixed(2)} </span>
            will be invoiced to me via {PROVIDER_LABEL} after my service is completed to my satisfaction.
          </span>
        </label>
      </div>

      {/* COMMUNICATIONS CONSENT */}
      <div className="mt-3 rounded-xl bg-[var(--brand-cream-soft)] border border-[#dcd5c6] p-5" data-testid="consent-communications-block">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.consentCommunications}
            onChange={(e) => update({ consentCommunications: e.target.checked })}
            className="w-5 h-5 accent-[var(--brand-teal)] mt-0.5 flex-shrink-0"
            data-testid="checkbox-consent-communications"
          />
          <span className="text-sm text-slate-800 leading-relaxed">
            I agree to receive <strong>booking confirmations, payment receipts, day-before reminders, and important service updates</strong> by <strong>email and text message</strong> at the address and phone number above from Top-Notch Pro Solutions. Message & data rates may apply. I can opt out at any time by replying STOP or contacting us. View our{" "}
            <Link to="/privacy" className="text-[var(--brand-teal)] underline font-semibold">Privacy Policy</Link>.
          </span>
        </label>
      </div>

      {/* TRUST BADGES */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-[#dcd5c6]">
          <Lock className="w-4 h-4 text-[var(--brand-gold)] mt-0.5 flex-shrink-0" />
          <span><strong className="text-[var(--brand-teal)]">256-Bit SSL.</strong> Your data is private & encrypted end-to-end.</span>
        </div>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-[#dcd5c6]">
          <BadgeCheck className="w-4 h-4 text-[var(--brand-gold)] mt-0.5 flex-shrink-0" />
          <span><strong className="text-[var(--brand-teal)]">PCI-Compliant.</strong> Card data is handled by {PROVIDER_LABEL}; we never see or store it.</span>
        </div>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-[#dcd5c6]">
          <Shield className="w-4 h-4 text-[var(--brand-gold)] mt-0.5 flex-shrink-0" />
          <span><strong className="text-[var(--brand-teal)]">Privacy Guaranteed.</strong> Your info is never shared.</span>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-lg bg-[var(--brand-teal)]/5 text-xs text-slate-700 space-y-1.5">
        <p><AlertCircle className="w-3.5 h-3.5 inline mr-1 text-[var(--brand-teal)]" /> <strong>Last-Minute Bookings:</strong> Requests within 48 hours of service are urgent-tier and prepaid in full.</p>
        <p><AlertCircle className="w-3.5 h-3.5 inline mr-1 text-[var(--brand-teal)]" /> <strong>Cancellation:</strong> Deposits are non-refundable for cancellations less than 24 hours before service.</p>
      </div>

      <button
        onClick={onSubmit}
        disabled={submitting || !form.withinServiceArea || !form.authorizeCardOnFile || !form.consentCommunications}
        className="btn-accent w-full justify-center mt-6 text-base py-4"
        data-testid="submit-booking-btn"
      >
        {submitting ? "Redirecting to secure checkout..." : <>Pay $50 Deposit & Lock My Slot <ArrowRight className="w-4 h-4" /></>}
      </button>

      <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed" data-testid="legal-disclaimer">
        By submitting, you confirm you are <strong>18 years or older</strong> and agree to our{" "}
        <a href="/terms" className="text-[var(--brand-teal)] underline hover:text-[var(--brand-gold-deep)]" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
        {" "}and{" "}
        <a href="/privacy" className="text-[var(--brand-teal)] underline hover:text-[var(--brand-gold-deep)]" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </p>
    </div>
  );
}

// ------------------ SUMMARY ------------------

function QuoteSummary({ form, quote }) {
  return (
    <div className="sticky top-24" data-testid="quote-summary">
      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>Your Quote</h3>
          <span className="text-xs uppercase font-bold tracking-wider px-2 py-1 rounded-full bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]">
            {form.serviceType === "deep" ? "Deep Clean" : "Standard"}
          </span>
        </div>

        <div className="space-y-3 text-sm">
          <Row label={`${form.bedrooms}${form.bedrooms === 6 ? "+" : ""} bed • ${form.bathrooms}${form.bathrooms === 5 ? "+" : ""} bath`} value={`$${quote.base.toFixed(2)}`} />
          {quote.discountAmount > 0 && (
            <Row label={`${quote.frequencyLabel} discount (${quote.discountPercent}%)`} value={`−$${quote.discountAmount.toFixed(2)}`} accent />
          )}
          {form.frequency === "weekly" && (
            <div className="rounded-lg bg-[var(--brand-gold)]/15 border border-[var(--brand-gold)]/40 px-3 py-2 flex items-center gap-2" data-testid="summary-promo">
              <span className="text-base">🎁</span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--brand-gold-deep)]">Bonus Unlocked</div>
                <div className="text-xs text-[var(--brand-teal)] font-semibold">5th weekly visit FREE</div>
              </div>
            </div>
          )}
          {quote.addOnsList.length > 0 && (
            <div className="border-t border-dashed border-[#dcd5c6] pt-3">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">Add-ons</div>
              {quote.addOnsList.map((a) => (
                <Row key={a.id} label={a.label} value={`+$${a.price.toFixed(2)}`} small />
              ))}
            </div>
          )}
          <div className="border-t border-[var(--brand-teal)]/20 pt-3 flex justify-between items-baseline">
            <span className="text-sm font-semibold text-slate-700">Service Estimate</span>
            <span className="text-3xl font-extrabold text-[var(--brand-teal)]" data-testid="summary-estimate" style={{ fontFamily: "Outfit" }}>${quote.serviceEstimate.toFixed(2)}</span>
          </div>
          <div className="rounded-lg bg-[var(--brand-gold)]/15 p-3 mt-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-700 font-semibold">Due today (deposit)</span>
              <span className="font-bold text-[var(--brand-gold-deep)]" data-testid="summary-deposit">$50.00</span>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-slate-700 font-semibold">Remaining after service</span>
              <span className="font-bold text-[var(--brand-teal)]" data-testid="summary-remaining">${quote.remainingBalance.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#e6e0d4] space-y-1.5 text-xs text-slate-500">
          <p className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-[var(--brand-gold)]" /> 256-bit SSL encryption</p>
          <p className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-[var(--brand-gold)]" /> Secure {PROVIDER_LABEL} checkout</p>
          <p className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-[var(--brand-gold)]" /> Privacy guaranteed</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent, small }) {
  return (
    <div className={`flex justify-between items-baseline ${small ? "text-xs" : "text-sm"}`}>
      <span className="text-slate-700">{label}</span>
      <span className={`font-bold ${accent ? "text-[var(--brand-gold-deep)]" : "text-[var(--brand-teal)]"}`}>{value}</span>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        {React.cloneElement(children, {
          className: `${children.props.className || ""} form-input`,
          style: { paddingLeft: 38 },
        })}
      </div>
    </div>
  );
}

// ------------------ SUCCESS ------------------

function BookingSuccess({ booking, quote, onReset }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-20" data-testid="booking-success">
      <div className="card-elevated p-10 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[var(--brand-gold)]/15 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-[var(--brand-gold-deep)]" />
        </div>
        <h1 className="heading-display text-3xl md:text-4xl">Booking Received!</h1>
        <p className="mt-3 text-slate-600">
          {IS_QUICKBOOKS
            ? "Your slot is held — complete the $50 deposit below to fully lock it in."
            : "Your slot is held pending your $50 deposit."}
        </p>
        <p className="mt-2 text-xs uppercase tracking-wider font-semibold text-slate-500">Booking ID</p>
        <p className="mt-1 font-mono text-sm text-[var(--brand-teal)]" data-testid="booking-id">{booking.id}</p>

        {IS_QUICKBOOKS && QB_PAYMENT_URL && (
          <div className="mt-8" data-testid="qb-pay-block">
            <a
              href={QB_PAYMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 rounded-xl bg-[#2CA01C] hover:bg-[#258D17] text-white font-bold text-lg shadow-lg shadow-[#2CA01C]/30 transition transform hover:scale-[1.02]"
              style={{ fontFamily: "Outfit" }}
              data-testid="qb-pay-button"
            >
              <Lock className="w-5 h-5" />
              Pay $50 Deposit with QuickBooks
              <ArrowRight className="w-5 h-5" />
            </a>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
              Opens QuickBooks Payments in a new tab. Once your deposit is received, we'll confirm your appointment by phone or email within 24 hours.
            </p>
          </div>
        )}

        <div className="mt-8 rounded-xl bg-[var(--brand-cream-soft)] p-5 text-left">
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Next step:</strong>{" "}
            {IS_QUICKBOOKS
              ? <>Click the green button above to complete your $50 deposit via QuickBooks. We'll contact you at <strong>{booking.email}</strong> or <strong>{booking.phone}</strong> within 24 hours to confirm.</>
              : <>Complete the $50 secure deposit to fully confirm your appointment. We'll contact you at <strong>{booking.email}</strong> or <strong>{booking.phone}</strong> within 24 hours.</>
            }
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 text-sm text-left">
          <div className="rounded-lg bg-white border border-[#dcd5c6] p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Estimate</div>
            <div className="font-extrabold text-[var(--brand-teal)] text-xl" style={{ fontFamily: "Outfit" }}>${booking.service_estimate.toFixed(2)}</div>
          </div>
          <div className="rounded-lg bg-white border border-[#dcd5c6] p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Deposit Due</div>
            <div className="font-extrabold text-[var(--brand-gold-deep)] text-xl" style={{ fontFamily: "Outfit" }}>$50.00</div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-outline" data-testid="success-home-btn">Return Home</Link>
          <button onClick={onReset} className="btn-primary" data-testid="success-new-btn">Book Another Service</button>
        </div>
      </div>
    </div>
  );
}
